/* Koffi Studio — Video Carousel (Coverflow / Inline Video)
*/

(() => {
  const qs = (el, s) => el.querySelector(s);
  const qsa = (el, s) => [...el.querySelectorAll(s)];
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  /* =========================
    TikTok embed support
  ========================= */
  function ensureTikTokEmbedScript() {
    return new Promise((resolve) => {
      if (window.__koffiTikTokLoaded) return resolve(true);

      const existing = document.querySelector('script[src*="tiktok.com/embed.js"]');
      if (existing) {
        existing.addEventListener('load', () => {
          window.__koffiTikTokLoaded = true;
          resolve(true);
        });
        if (existing.dataset.loaded === 'true') {
          window.__koffiTikTokLoaded = true;
          return resolve(true);
        }
        return;
      }

      const s = document.createElement('script');
      s.src = 'https://www.tiktok.com/embed.js';
      s.async = true;
      s.onload = () => {
        s.dataset.loaded = 'true';
        window.__koffiTikTokLoaded = true;
        resolve(true);
      };
      document.head.appendChild(s);
    });
  }

  async function refreshTikTokEmbeds(root) {
    if (!root) return;
    const wraps = root.querySelectorAll('[data-tiktok-embed]');
    if (!wraps.length) return;

    const bqs = root.querySelectorAll('blockquote.tiktok-embed');
    if (!bqs.length) return;

    await ensureTikTokEmbedScript();

    wraps.forEach((wrap) => {
      const bq = wrap.querySelector('blockquote.tiktok-embed');
      if (!bq) return;
      if (wrap.querySelector('iframe')) return;
      wrap.innerHTML = bq.outerHTML;
    });

    try {
      if (window.tiktokEmbed && typeof window.tiktokEmbed.load === 'function') {
        window.tiktokEmbed.load();
      }
    } catch (e) {}
  }

  /* =========================
    Video helpers (NO AUTOPLAY)
  ========================= */
  function getInlineVideo(card) {
    return (
      card.querySelector('.koffi-vc__videoWrap video') ||
      card.querySelector('video')
    );
  }

  function paintFirstFrame(video) {
    if (!video || video.__koffiPainted) return;
    video.__koffiPainted = true;

    // For poster-like rendering (no autoplay)
    video.muted = false;
    video.playsInline = true;

    const onMeta = () => {
      try {
        const t = Math.min(0.05, (video.duration || 1) * 0.001);
        video.currentTime = t;
      } catch (e) {}
    };

    const onSeeked = () => {
      try { video.pause(); } catch (e) {}
      cleanup();
    };

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('seeked', onSeeked);
    };

    video.addEventListener('loadedmetadata', onMeta, { once: true });
    video.addEventListener('seeked', onSeeked);

    try { video.load?.(); } catch (e) {}
  }

  function stopAndReset(card) {
    const v = getInlineVideo(card);
    if (!v) return;
    try { v.pause(); } catch (e) {}
    try { v.currentTime = 0; } catch (e) {}
    v.muted = false;
    v.playsInline = true;
    paintFirstFrame(v);
  }

  function togglePlay(card) {
    const v = getInlineVideo(card);
    if (!v) return;

    v.playsInline = true;

    if (v.paused) {
      // user gesture: allowed to unmute
      v.muted = false;
      const p = v.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      try { v.pause(); } catch (e) {}
    }
  }

  /* =========================
    Carousel init
  ========================= */
  function init(section) {
    if (!section) return;

    const track = qs(section, '[data-vc-track]');
    const viewport = qs(section, '[data-vc-viewport]');
    const prevBtn = qs(section, '[data-vc-prev]');
    const nextBtn = qs(section, '[data-vc-next]');
    const dotsWrap = qs(section, '[data-vc-dots]');

    const modal = qs(section, '[data-vc-modal]');
    const stage = qs(section, '[data-vc-stage]');
    const closeEls = modal ? qsa(section, '[data-vc-close]') : [];

    if (!track) return;

    const loopEnabled = section.dataset.loop === 'true';
    const visibleSide = parseInt(section.dataset.visibleSide || '2', 10);
    const startIndex = parseInt(section.dataset.start || '0', 10);

    const readVarNum = (name, fallback) => {
      const v = getComputedStyle(section).getPropertyValue(name);
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const getSpeed = () => {
      const v = getComputedStyle(section).getPropertyValue('--koffi-speed');
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 420;
    };

    let locked = false;

    // Real cards BEFORE cloning
    let realCards = qsa(track, '[data-vc-card]');
    if (!realCards.length) return;
    const realCount = realCards.length;

    /* ---------- Build loop clones once ---------- */
    if (loopEnabled && !track.__koffiLoopBuilt) {
      track.__koffiLoopBuilt = true;

      const before = [];
      const after = [];

      realCards.forEach((card) => {
        const a = card.cloneNode(true);
        a.classList.add('is-clone');
        after.push(a);

        const b = card.cloneNode(true);
        b.classList.add('is-clone');
        before.push(b);
      });

      before.reverse().forEach((c) => track.prepend(c));
      after.forEach((c) => track.append(c));
    }

    // Cards INCLUDING clones
    let cards = qsa(track, '[data-vc-card], .is-clone');

    // Index lives in the middle band when looping
    const startReal = clamp(startIndex, 0, realCount - 1);
    let index = loopEnabled ? (realCount + startReal) : startReal;

    /* ---------- Dots (real only) ---------- */
    let dots = [];
    const dotsEnabled = section.dataset.dots === 'true';

    if (dotsWrap && dotsEnabled && !dotsWrap.__koffiBuilt) {
      dotsWrap.__koffiBuilt = true;
      dotsWrap.innerHTML = '';

      dots = Array.from({ length: realCount }).map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'koffi-vc__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => goToReal(i));
        dotsWrap.appendChild(b);
        return b;
      });
    } else if (dotsWrap && dotsEnabled) {
      dots = qsa(dotsWrap, '.koffi-vc__dot');
    }

    const toRealIndex = (globalIndex) => {
      if (!loopEnabled) return clamp(globalIndex, 0, realCount - 1);
      return ((globalIndex % realCount) + realCount) % realCount;
    };

    function closestTargetInBands(desiredReal) {
      if (!loopEnabled) return clamp(desiredReal, 0, realCount - 1);

      const a = desiredReal;
      const b = desiredReal + realCount;
      const c = desiredReal + realCount * 2;

      const candidates = [a, b, c].sort((x, y) => Math.abs(x - index) - Math.abs(y - index));
      const best = candidates[0];

      if (best !== b && Math.abs(best - index) - Math.abs(b - index) <= 1) return b;
      return best;
    }

    /* ---------- Jump without flicker ---------- */
    function jumpTo(newIndex) {
      cards.forEach((card) => (card.style.transition = 'none'));
      index = newIndex;
      render();
      void section.offsetHeight;
      requestAnimationFrame(() => {
        cards.forEach((card) => (card.style.transition = ''));
      });
    }

    function normalizeToMiddleBand() {
      if (!loopEnabled) return;
      if (index < realCount) jumpTo(index + realCount);
      else if (index >= realCount * 2) jumpTo(index - realCount);
    }

    /* =========================
      Modal (embeds only)
    ========================= */
    function openModalFromCard(card) {
      if (!modal || !stage) return;
      const tpl = card.querySelector('[data-vc-template]');
      if (!tpl) return;

      stage.innerHTML = '';
      stage.appendChild(tpl.content.cloneNode(true));

      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';

      refreshTikTokEmbeds(stage);
    }

    function closeModal() {
      if (!modal || !stage) return;
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      stage.innerHTML = '';
    }

    if (modal) closeEls.forEach((el) => el.addEventListener('click', closeModal));

    /* =========================
      Render coverflow (NO AUTOPLAY)
    ========================= */
    function render() {
      const cardW = readVarNum('--koffi-card-w', 320);
      const step = readVarNum('--koffi-step', cardW * 0.62);

      const sideScale = readVarNum('--koffi-side-scale', 0.82);
      const sideDim = readVarNum('--koffi-side-dim', 0.25);
      const rotateDeg = readVarNum('--koffi-rotate', 8);

      cards.forEach((card, i) => {
        const off = i - index;
        const abs = Math.abs(off);
        const inRange = abs <= visibleSide;

        const scale = abs === 0 ? 1 : Math.pow(sideScale, abs);
        const x = off * step;
        const rot = abs === 0 ? 0 : (off > 0 ? -rotateDeg : rotateDeg);

        card.style.zIndex = String(200 - abs);
        card.style.opacity = inRange ? '1' : '0';
        card.style.pointerEvents = inRange ? 'auto' : 'none';
        card.style.filter = `brightness(${1 - (abs === 0 ? 0 : sideDim)})`;
        card.style.transform = `translateX(${x}px) scale(${scale}) rotateY(${rot}deg)`;

        const isActive = abs === 0;
        card.classList.toggle('is-active', isActive);

        // NO autoplay: only stop/reset non-active
        if (!isActive) stopAndReset(card);
        else {
          // active: ensure poster/first frame is ready (but DO NOT play)
          const v = getInlineVideo(card);
          if (v) paintFirstFrame(v);
        }

        // Bind click once
        const btn = card.querySelector('[data-vc-open]');
        if (btn && !btn.__koffiBound) {
          btn.__koffiBound = true;

          btn.addEventListener('click', () => {
            // If clicked side card, center it
            if (!card.classList.contains('is-active')) {
              go(i);
              return;
            }

            // If embed template exists -> open modal
            if (card.querySelector('[data-vc-template]')) {
              openModalFromCard(card);
              return;
            }

            // Toggle play ONLY on active card
            togglePlay(card);
          });

          if (btn.getAttribute('role') === 'button' && !btn.__koffiKeyBound) {
            btn.__koffiKeyBound = true;
            btn.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                btn.click();
              }
            });
          }
        }
      });

      if (dots.length) {
        const ri = toRealIndex(index);
        dots.forEach((d, i) => d.classList.toggle('is-active', i === ri));
      }

      refreshTikTokEmbeds(section);
    }

    /* =========================
      Navigation
    ========================= */
    function go(nextIndex) {
      if (locked) return;
      locked = true;

      index = loopEnabled ? nextIndex : clamp(nextIndex, 0, realCount - 1);
      render();

      window.setTimeout(() => {
        if (loopEnabled) normalizeToMiddleBand();
        locked = false;
      }, getSpeed() + 30);
    }

    function goStep(delta) {
      go(index + delta);
    }

    function goToReal(realIdx) {
      const r = clamp(realIdx, 0, realCount - 1);
      const target = loopEnabled ? closestTargetInBands(r) : r;
      go(target);
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goStep(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => goStep(1));

    // Keyboard: allow nav when modal closed
    document.addEventListener('keydown', (e) => {
      if (modal && !modal.hidden) {
        if (e.key === 'Escape') closeModal();
        return;
      }
      if (e.key === 'ArrowLeft') goStep(-1);
      if (e.key === 'ArrowRight') goStep(1);
    });

    // Swipe
    if (viewport) {
      let startX = 0;
      let dragging = false;

      viewport.addEventListener('pointerdown', (e) => {
        dragging = true;
        startX = e.clientX;
        viewport.setPointerCapture(e.pointerId);
      });

      viewport.addEventListener('pointerup', (e) => {
        if (!dragging) return;
        dragging = false;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 30) goStep(dx < 0 ? 1 : -1);
      });

      viewport.addEventListener('pointercancel', () => (dragging = false));
    }

    // Paint frames early (so cards show first frame)
    cards.forEach((card) => {
      const v = getInlineVideo(card);
      if (v) paintFirstFrame(v);
    });

    // Initial render
    render();

    // Start clean in middle band
    if (loopEnabled) jumpTo(index);
  }

  function initAll() {
    document.querySelectorAll('.koffi-vc').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target?.querySelector?.('.koffi-vc');
    if (el) init(el);
  });
})();
